import { Box } from '@mantine/core';
import type { UserOut as IUser } from '@repo/api-codegen/v2';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import { noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../api';
import {
	useExtendedUserList,
	useGuestUserList,
} from '../../../api/hooks/user/useExtendedUserList';
import {
	getAssigneeGroupOptions,
	getAssigneeOptions,
} from '../../EntityPageLayout/utils';

interface AssigneeSelectorProps {
	onChange?: (key: string) => (value: string | string[]) => void;
	initialValue?: string;
}

export function AssigneeSelector({
	onChange,
	initialValue,
}: AssigneeSelectorProps) {
	const { isGuestUser } = useAuthUser();
	const { usersVisibleToGuests } = useGuestUserList();
	const { activeUsers, userGroups } = useExtendedUserList();

	const visibleUsers = (isGuestUser
		? usersVisibleToGuests
		: activeUsers) as unknown as IUser[];

	const assignees = useMemo(() => {
		const userOptions = getAssigneeOptions(visibleUsers, noop);
		const groupOptions = getAssigneeGroupOptions(userGroups ?? [], noop);
		return [...userOptions, ...groupOptions];
	}, [visibleUsers, userGroups]);

	const handleChange = useCallback(
		(value: string) => {
			const selectedOption = assignees.find((opt) => opt.value === value);
			if (selectedOption?.group === 'groups') {
				onChange?.('assigned_to_group')(value);
				onChange?.('assigned_to')('');
			} else {
				onChange?.('assigned_to')(value);
				onChange?.('assigned_to_group')('');
			}
		},
		[assignees, onChange]
	);

	return (
		<Box maw={256}>
			<SingleSelector
				placeholder="Add assignee"
				placeholderIconName="user"
				variant={initialValue ? 'default' : 'tertiary'}
				iconType="avatar"
				isViewerUser={false}
				options={assignees}
				initialSelected={initialValue}
				property="owner"
				searchable
				onChange={handleChange}
			/>
		</Box>
	);
}
